import { action, observable } from 'mobx'
import { required, validationContext } from 'validx'
import type { BusinessInfo } from './BusinessInfo'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  mustBeValidEin,
  mustBeInThePast,
} from '@/utils/validx-util'

/**
 * View model for editing business information.
 */
export class BusinessInfoEditorViewModel {
  @observable accessor businessName = ''
  @observable accessor dateOfIncorporation: Date | null = null
  @observable accessor employerIdentificationNumber = ''

  readonly validation = validationContext(this, {
    businessName: [
      required('Business name is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Business name must not exceed 100 characters'),
    ],
    dateOfIncorporation: [
      required('Date of incorporation is required'),
      mustBeInThePast('Date of incorporation must be in the past'),
    ],
    employerIdentificationNumber: [
      required('EIN is required'),
      mustBeValidEin('EIN must be in format XX-XXXXXXX'),
    ],
  })

  /**
   * Resets the state of the view model to that of the given
   * business info, or defaults if none is specified.
   */
  @action.bound
  reset(info?: BusinessInfo) {
    this.businessName = info?.businessName ?? ''
    this.dateOfIncorporation = info?.dateOfIncorporation ?? null
    this.employerIdentificationNumber = info?.employerIdentificationNumber ?? ''
    this.validation.reset()
  }

  /**
   * Maps the state of the VM to {@link BusinessInfo}.
   */
  toBusinessInfo(): BusinessInfo {
    return {
      businessName: this.businessName,
      dateOfIncorporation: this.dateOfIncorporation,
      employerIdentificationNumber: this.employerIdentificationNumber,
    }
  }
}
